import { addDays, format, isSameDay, parseISO } from 'date-fns';

/**
 * MET-468: the date the quote page's automatic re-date sends. It is before
 * every possible quoteExpireDate and before "today" in any timezone, so the
 * backend clamp (forms-backend updateEffectiveDate.ts) stores start-of-today in
 * the business timezone (E1), its pre-clamp expiry check can never refuse it,
 * and HubSpot/audit record the stored date rather than this value.
 */
export const EFFECTIVE_DATE_AUTO_UPDATE_SENTINEL = '2000-01-01';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Accepts the date formats the quote page receives:
 * - `Date.toDateString()` ("Thu Oct 22 2026"), used by GetQuoteOk.quoteExpireDate
 *   (RQ getQuoteInfo.ts) and BusinessInfo.coverageStartDate (forms-backend
 *   getBusinessInfo.ts); `new Date` reads it as local midnight;
 * - `yyyy-MM-dd`, read as local midnight (`new Date` would read it as UTC);
 * - full ISO, used by GetQuoteExpired.quoteExpireDate (RQ getIsQuoteExpired.ts),
 *   read as that instant.
 * Returns undefined for empty or unparseable input.
 */
export function parseCalendarDate(
  value: string | Date | null | undefined
): Date | undefined {
  if (!value) return undefined;
  const date =
    value instanceof Date
      ? value
      : DATE_ONLY.test(value)
      ? parseISO(value)
      : new Date(value);
  return isNaN(date.getTime()) ? undefined : date;
}

/** "Oct 9, 2026"; undefined when the input does not parse. */
export function formatEffectiveDate(
  value: string | Date | null | undefined
): string | undefined {
  const date = parseCalendarDate(value);
  return date ? format(date, 'MMM d, yyyy') : undefined;
}

/** Renewals are locked to their start date; otherwise today to today + 60. */
export function getEffectiveDatePickerBounds({
  transactionType,
  coverageStartDate,
  today
}: {
  transactionType: string;
  coverageStartDate: Date;
  today: Date;
}): { min: Date; max: Date } {
  if (transactionType === 'Renewal') {
    return { min: coverageStartDate, max: coverageStartDate };
  }
  return { min: today, max: addDays(today, 60) };
}

/**
 * Both dates are start-of-day, so a date equal to the quote expiry is already
 * too late (the Buy Now rule). The expiry is parsed with `new Date`, exactly as
 * Buy Now always has, so the rule stays identical for every input format.
 */
export function isOnOrAfterQuoteExpiry(
  date: Date,
  quoteExpireDate: string
): boolean {
  const expiry = new Date(quoteExpireDate);
  return !isNaN(expiry.getTime()) && date >= expiry;
}

/**
 * The earliest of the group's quote expiry dates, as the original string.
 * Inputs may mix toDateString and ISO formats; unparseable ones are skipped.
 */
export function getEarliestQuoteExpireDate(
  dates: Array<string | null | undefined>
): string | undefined {
  let earliest: { value: string; date: Date } | undefined;
  dates.forEach((value) => {
    const date = parseCalendarDate(value);
    if (value && date && (!earliest || date < earliest.date)) {
      earliest = { value, date };
    }
  });
  return earliest?.value;
}

export function isSameCalendarDay(a: Date, b: Date): boolean {
  return isSameDay(a, b);
}
